import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '@/hooks';
import { clearSession } from '@/redux/slices/sessionStateSlice';
import { setColorMode, type ColorMode } from '@/redux/slices/colorModeSlice';
import { useLogoutMutation, useGetTenantSettingsQuery } from '@/redux/internal-services/onboardingApi';
import { useGetMeQuery, useUpdateMeMutation } from '@/redux/internal-services/usersApi';
import { NotificationBell } from '@/components/Notifications';
import { cn } from '@/utils/cn';

interface TopBarProps {
  /** Called when the hamburger button is tapped (mobile/tablet only). */
  onMenuClick?: () => void;
  /** Dark variant — matches the AI Chat surface so the header merges into it. */
  dark?: boolean;
  /** Ref callback for the actions slot rendered next to the profile menu — pages portal
   *  controls (e.g. the AI Chat view toggle) into it via TopBarSlotContext. */
  slotRef?: React.RefCallback<HTMLDivElement>;
}

const TopBar: React.FC<TopBarProps> = ({ onMenuClick, dark = false, slotRef }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const userName = useAppSelector((state) => state.sessionState.userName);
  const userEmail = useAppSelector((state) => state.sessionState.userEmail);
  const [logout, { isLoading }] = useLogoutMutation();

  // Tenant branding shown on the LEFT of the header (desktop/web only). Platform roles are
  // cross-tenant, so they see no tenant name/logo. The company name comes from tenant settings;
  // the logo from the brand store (uploaded logo) with a lettered fallback badge.
  const roles = useAppSelector((s) => s.sessionState.roles);
  const isPlatformRole = roles.includes('platform_admin') || roles.includes('platform_support');
  const logoLightUrl = useAppSelector((s) => s.brand.logoLightUrl);
  const logoDarkUrl = useAppSelector((s) => s.brand.logoDarkUrl);
  const { data: tenantSettings } = useGetTenantSettingsQuery(undefined, { skip: isPlatformRole });
  const companyName = (
    (tenantSettings?.data as Record<string, unknown> | undefined)?.company_name as string | undefined
  )?.trim();
  const tenantLogo = (logoLightUrl || logoDarkUrl || '').trim();

  const colorMode: ColorMode = useAppSelector((s) => s.colorMode?.mode ?? 'system');
  const { data: me } = useGetMeQuery();
  const [updateMe] = useUpdateMeMutation();

  const handleSetMode = (m: ColorMode) => {
    dispatch(setColorMode(m));
    updateMe({ theme_mode: m });
  };

  const cycleMode = () => {
    const next: Record<ColorMode, ColorMode> = { light: 'dark', dark: 'system', system: 'light' };
    handleSetMode(next[colorMode]);
  };

  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleEditProfile = () => {
    setOpen(false);
    navigate('/profile');
  };

  const handleLogout = async () => {
    setOpen(false);
    try {
      await logout().unwrap();
    } catch {
      // Ignore network/backend errors — local session is cleared regardless.
    } finally {
      dispatch(clearSession());
      navigate('/login', { replace: true });
    }
  };

  return (
    <header
      className={cn(
        'flex h-14 items-center justify-between border-b px-4 md:h-16 md:px-6',
        dark
          ? 'border-white/10 bg-chat'
          : 'border-[#EDEDED] dark:border-white/10 bg-ui-card'
      )}
    >
      {/* Left cluster: hamburger (mobile) + tenant company logo & name. Kept in one group so
          justify-between splits it cleanly against the right-hand controls. */}
      <div className="flex min-w-0 items-center gap-2">
        {/* Hamburger — mobile opens the drawer; desktop toggles sidebar collapse */}
        <button
          type="button"
          onClick={onMenuClick}
          className={cn(
            'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-dark/40',
            dark
              ? 'text-gray-300 hover:bg-white/10'
              : 'text-ui-text2 hover:bg-ui-hover'
          )}
          aria-label="Toggle navigation"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Tenant company logo + name — desktop only (hidden on mobile/tablet). Hidden for
            platform roles (cross-tenant) too. */}
        {!isPlatformRole && companyName && (
          <div className="hidden min-w-0 items-center gap-2 lg:flex">
            {tenantLogo ? (
              <img
                src={tenantLogo}
                alt={companyName}
                className="h-8 w-8 shrink-0 rounded-md object-contain"
              />
            ) : (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-light text-sm font-bold text-brand-dark dark:bg-white/10 dark:text-white">
                {companyName.charAt(0).toUpperCase()}
              </div>
            )}
            <span
              // Follow the actual color mode (not the `dark` chat-surface flag): dark text in light
              // mode, white in dark mode — legible on the header in both.
              className="max-w-[180px] truncate text-sm font-semibold text-gray-900 dark:text-white"
            >
              {companyName}
            </span>
          </div>
        )}
      </div>

      {/* Right cluster: page-provided actions slot + user menu */}
      <div className="flex items-center gap-1 md:gap-2">
        {/* Theme mode cycle button — sits left of the chat/slot icon */}
        <button
          type="button"
          onClick={cycleMode}
          title={colorMode === 'light' ? 'Light mode — click for dark' : colorMode === 'dark' ? 'Dark mode — click for auto (follows device)' : 'Auto mode (follows device) — click for light'}
          aria-label={`Color mode: ${colorMode}`}
          className={cn(
            'flex h-9 w-9 items-center justify-center rounded-lg transition-colors',
            dark ? 'text-gray-300 hover:bg-white/10' : 'text-ui-text2 hover:bg-ui-hover'
          )}
        >
          {colorMode === 'light' && (
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
            </svg>
          )}
          {colorMode === 'dark' && (
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
          {colorMode === 'system' && (
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          )}
        </button>

        <div ref={slotRef} className="flex items-center gap-1" />

        <NotificationBell />

        {/* User menu */}
        <div ref={menuRef} className="relative">
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="flex items-center gap-3 rounded-full focus:outline-none focus:ring-2 focus:ring-brand-dark/40"
            >
              <span className="hidden text-sm font-medium text-gray-900 dark:text-white sm:block">
                {userName}
              </span>

              <div className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full bg-gray-200 text-xs font-semibold text-gray-900 dark:bg-gray-700 dark:text-white">
                {me?.profile_picture_url ? (
                  <img
                    src={me.profile_picture_url}
                    alt={userName ?? ""}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  userName?.charAt(0).toUpperCase()
                )}
              </div>
            </button>

            {open && (
              <div className="absolute right-0 z-50 mt-2 w-60 overflow-hidden rounded-xl border border-[#EDEDED] dark:border-white/10 bg-ui-card shadow-lg">
                {/* User info */}
                <div className="border-b border-[#EDEDED] dark:border-white/10 px-4 py-3">
                  <p className="truncate text-sm font-medium text-[#272727] dark:text-gray-100">{userName}</p>
                  {userEmail && <p className="truncate text-xs text-ui-text3">{userEmail}</p>}
                </div>

                {/* Appearance / color mode */}
                <div className="border-b border-[#EDEDED] dark:border-white/10 px-4 py-3">
                  <p className="mb-2 text-xs font-medium text-ui-text3">{t('colorMode.label')}</p>
                  <div className="flex gap-1">
                    {(['light', 'dark', 'system'] as const).map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => handleSetMode(m)}
                        title={t(`colorMode.${m}`)}
                        className={cn(
                          'flex flex-1 flex-col items-center gap-1 rounded-lg py-2 text-[11px] font-medium transition-colors',
                          colorMode === m
                            ? 'bg-brand-light text-brand-dark dark:bg-white/10 dark:text-white'
                            : 'text-ui-text2 hover:bg-ui-hover'
                        )}
                      >
                        {m === 'light' && (
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M12 8a4 4 0 100 8 4 4 0 000-8z" />
                          </svg>
                        )}
                        {m === 'dark' && (
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                          </svg>
                        )}
                        {m === 'system' && (
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        )}
                        <span className="capitalize">{t(`colorMode.${m}`)}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Edit profile */}
                <button
                  type="button"
                  onClick={handleEditProfile}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm font-medium text-[#272727] dark:text-gray-100 hover:bg-[#F7F4FF] dark:hover:bg-white/10"
                >
                  <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  Edit Profile
                </button>

                {/* Logout */}
                <button
                  type="button"
                  onClick={handleLogout}
                  disabled={isLoading}
                  className="flex w-full items-center gap-2 px-4 py-3 text-left text-sm font-medium text-[#CF4148] hover:bg-[#F7F4FF] dark:hover:bg-white/10 disabled:opacity-50"
                >
                  <svg className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  {t('auth.logout')}
                </button>
              </div>
            )}
        </div>
      </div>
    </header>
  );
};

export default TopBar;
